import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, Check, X, Clock, Euro, Users, Heart, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../services/api';

const API = API_BASE_URL;

const Payment = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Check if returning from Stripe
  const sessionId = searchParams.get('session_id');
  const isSuccess = window.location.pathname.includes('/payment/success');
  const isCancel = window.location.pathname.includes('/payment/cancel');

  useEffect(() => {
    fetchPaymentPackages();
    
    // Check payment status if returning from Stripe
    if (sessionId && isSuccess) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId, isSuccess]);

  const fetchPaymentPackages = async () => {
    try {
      const response = await axios.get(`${API}/api/payments/packages`);
      setPackages(response.data.packages);
    } catch (error) {
      console.error('Erreur lors du chargement des packages:', error);
      setError('Impossible de charger les options de paiement');
    }
  };

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus({
        type: 'warning',
        message: 'Vérification du paiement en cours... Veuillez patienter ou vérifier votre email.'
      });
      return;
    }

    try {
      const response = await axios.get(`${API}/api/payments/checkout-status/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('padaid_user_token')}`
        }
      });

      if (response.data.payment_status === 'paid') {
        setPaymentStatus({
          type: 'success',
          message: 'Paiement réussi ! Merci pour votre soutien à PADAID.',
          details: response.data
        });
        return;
      } else if (response.data.status === 'expired') {
        setPaymentStatus({
          type: 'error',
          message: 'La session de paiement a expiré. Veuillez recommencer.'
        });
        return;
      }

      // Continue polling if payment is still pending
      setPaymentStatus({
        type: 'pending',
        message: 'Traitement du paiement en cours...'
      });
      
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Erreur lors de la vérification du paiement:', error);
      setPaymentStatus({
        type: 'error',
        message: 'Erreur lors de la vérification du paiement.'
      });
    }
  };

  const handlePayment = async () => {
    if (!selectedPackage) {
      setError('Veuillez sélectionner un type de paiement');
      return;
    }

    if (selectedPackage.amount === null && (!customAmount || parseFloat(customAmount) <= 0)) {
      setError('Veuillez entrer un montant valide');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const paymentData = {
        package_id: selectedPackage.id,
        origin_url: window.location.origin,
        custom_amount: selectedPackage.amount === null ? parseFloat(customAmount) : undefined
      };

      const response = await axios.post(`${API}/api/payments/create-checkout-session`, paymentData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('padaid_user_token')}`
        }
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors de la création du paiement');
      setLoading(false);
    }
  };

  const getPackageIcon = (packageId) => {
    switch (packageId) {
      case 'membre_mensuel':
        return <Users className="h-6 w-6" />;
      case 'supporter_mensuel':
        return <Heart className="h-6 w-6" />;
      case 'don_ponctuel':
        return <Gift className="h-6 w-6" />;
      case 'don_mensuel':
        return <Heart className="h-6 w-6" />;
      default:
        return <CreditCard className="h-6 w-6" />;
    }
  };

  const getPackageColor = (packageId) => {
    switch (packageId) {
      case 'membre_mensuel':
        return 'border-primary/20 hover:border-primary/40';
      case 'supporter_mensuel':
        return 'border-secondary/20 hover:border-secondary/40';
      case 'don_ponctuel':
        return 'border-accent/20 hover:border-accent/40';
      case 'don_mensuel':
        return 'border-destructive/20 hover:border-destructive/40';
      default:
        return 'border-gray-200 hover:border-gray-300';
    }
  };

  if (isCancel) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-primary/5 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="h-8 w-8 text-yellow-500" />
            </div>
            <CardTitle>Paiement annulé</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-6">
              Votre paiement a été annulé. Aucun montant n'a été débité.
            </p>
            <Button onClick={() => navigate(-1)} className="w-full">
              Retour
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentStatus) {
    const statusConfig = {
      success: { icon: Check, color: 'text-green-500', bgColor: 'bg-green-500/10' },
      error: { icon: X, color: 'text-red-500', bgColor: 'bg-red-500/10' },
      pending: { icon: Clock, color: 'text-yellow-500', bgColor: 'bg-yellow-500/10' },
      warning: { icon: Clock, color: 'text-orange-500', bgColor: 'bg-orange-500/10' }
    };

    const config = statusConfig[paymentStatus.type];
    const StatusIcon = config.icon;

    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-primary/5 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className={`w-16 h-16 ${config.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
              <StatusIcon className={`h-8 w-8 ${config.color}`} />
            </div>
            <CardTitle>
              {paymentStatus.type === 'success' ? 'Paiement réussi !' :
               paymentStatus.type === 'error' ? 'Erreur de paiement' :
               'Traitement en cours'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-6">
              {paymentStatus.message}
            </p>
            
            {paymentStatus.details && (
              <div className="bg-muted/50 p-4 rounded-lg mb-6 text-left">
                <p className="text-sm"><strong>Montant :</strong> {(paymentStatus.details.amount_total / 100).toFixed(2)} €</p>
                <p className="text-sm"><strong>Session :</strong> {paymentStatus.details.session_id}</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Button onClick={() => navigate('/home')} className="w-full">
                Retour à l'accueil
              </Button>
              {paymentStatus.type === 'error' && (
                <Button onClick={() => window.location.reload()} variant="outline" className="w-full">
                  Réessayer
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-primary/5 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Soutenir PADAID
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Choisissez comment vous souhaitez contribuer à notre mission d'aide humanitaire et de développement durable en Afrique.
          </p>
        </div>

        {error && (
          <Alert className="mb-6" variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Payment Packages */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {packages.map((pkg) => (
            <Card
              key={pkg.id}
              className={`cursor-pointer transition-all duration-200 ${
                selectedPackage?.id === pkg.id 
                  ? 'ring-2 ring-primary border-primary/40' 
                  : getPackageColor(pkg.id)
              }`}
              onClick={() => setSelectedPackage(pkg)}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {getPackageIcon(pkg.id)}
                    <div className="text-sm font-medium">{pkg.name}</div>
                  </div>
                  {pkg.recurring && (
                    <Badge variant="secondary" className="text-xs">
                      Mensuel
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="mb-4">
                  {pkg.description}
                </CardDescription>
                
                <div className="text-center">
                  {pkg.amount !== null ? (
                    <div className="text-2xl font-bold text-primary">
                      {pkg.amount}€
                      {pkg.recurring && <span className="text-sm text-muted-foreground">/mois</span>}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Montant personnalisé
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Custom Amount Input */}
        {selectedPackage && selectedPackage.amount === null && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Montant personnalisé</CardTitle>
              <CardDescription>
                Choisissez le montant de votre {selectedPackage.id.includes('mensuel') ? 'contribution mensuelle' : 'don'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                <Label htmlFor="amount" className="sr-only">Montant</Label>
                <div className="relative flex-1">
                  <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="amount"
                    type="number"
                    placeholder="0.00"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="pl-10"
                    min="1"
                    step="0.01"
                  />
                </div>
                <span className="text-sm text-muted-foreground">€</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Button */}
        {selectedPackage && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="text-lg font-medium">
                  Vous êtes sur le point de :
                </div>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <div className="font-semibold">{selectedPackage.name}</div>
                  <div className="text-sm text-muted-foreground mb-2">
                    {selectedPackage.description}
                  </div>
                  <div className="text-2xl font-bold text-primary">
                    {selectedPackage.amount !== null 
                      ? `${selectedPackage.amount}€` 
                      : `${customAmount || '0'}€`
                    }
                    {selectedPackage.recurring && (
                      <span className="text-sm text-muted-foreground">/mois</span>
                    )}
                  </div>
                </div>

                <Button
                  onClick={handlePayment}
                  disabled={loading || (selectedPackage.amount === null && (!customAmount || parseFloat(customAmount) <= 0))}
                  className="w-full bg-primary hover:bg-primary/90 text-lg py-6"
                  size="lg"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-foreground mr-3"></div>
                      Redirection vers Stripe...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <CreditCard className="mr-2 h-5 w-5" />
                      Procéder au paiement
                    </div>
                  )}
                </Button>

                <p className="text-xs text-muted-foreground">
                  Paiement sécurisé par Stripe • Vous serez redirigé vers une page de paiement sécurisée
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Payment;